import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { printBuffer, isFaxStatus } from '@mummssoftware/utils/api/Printing';
import { APIAlert } from '../Alert';
import { FaxModal } from './FaxModal';
import defaults from '../translations/en.json';
const FaxContext = /*#__PURE__*/createContext(null);
export const useFax = () => {
  const ctx = useContext(FaxContext);

  if (!ctx) {
    throw new Error('useFax must be used within a FaxProvider');
  }

  return ctx;
};

function formOptionToInstructions(option, senderName) {
  var _option$subjectText;

  if (!option || !option.recipientnumber || !option.recipientname) return null;
  return {
    recipientNumber: option.recipientnumber,
    recipientName: option.recipientname,
    coverPageNotes: option.noteText,
    coverPageReference: option.referenceText,
    faxSubject: (_option$subjectText = option.subjectText) !== null && _option$subjectText !== void 0 ? _option$subjectText : '',
    senderName
  };
}

export const FaxProvider = ({
  config,
  children
}) => {
  const {
    siteFaxList,
    fetchFaxList,
    isFaxEnabled,
    getSenderName
  } = config;
  const [open, setOpen] = useState(false);
  const [faxOption, setFaxOption] = useState(null);
  const [faxAlertInfo, setFaxAlertInfo] = useState(null);
  const pendingResolveRef = useRef(null);
  const ensureFaxList = useCallback(async () => {
    if (siteFaxList && siteFaxList.length > 0) return;
    await fetchFaxList();
  }, [fetchFaxList, siteFaxList]);
  const requestFax = useCallback(async _options => {
    if (!isFaxEnabled) {
      setFaxAlertInfo({
        text: defaults.faxNotConfigured,
        severity: 'info',
        persistent: true
      });
      return null;
    }

    return new Promise(resolve => {
      pendingResolveRef.current = resolve;
      setFaxOption(null);
      setOpen(true);
      ensureFaxList();
    });
  }, [isFaxEnabled, ensureFaxList]);
  const handleClose = useCallback(() => {
    setOpen(false);

    if (pendingResolveRef.current) {
      pendingResolveRef.current(null);
      pendingResolveRef.current = null;
    }

    setFaxOption(null);
  }, []);
  const handleSubmit = useCallback(() => {
    const senderName = getSenderName();
    const instructions = formOptionToInstructions(faxOption, senderName);

    if (pendingResolveRef.current) {
      pendingResolveRef.current(instructions);
      pendingResolveRef.current = null;
    }

    setOpen(false);
    setFaxOption(null);
  }, [faxOption, getSenderName]);
  const submitPrintOrFax = useCallback(async options => {
    var _instructions;

    let instructions = options.faxInstructions;

    if (options.wantFax && instructions === undefined) {
      instructions = await requestFax();
    }

    if (options.wantFax && instructions === null) {
      return {
        handled: 'cancelled'
      };
    }

    const result = await options.getPrintPromise((_instructions = instructions) !== null && _instructions !== void 0 ? _instructions : undefined);

    if (isFaxStatus(result)) {
      const status = result;
      setFaxAlertInfo({
        text: status.message,
        severity: status.isSuccess ? 'success' : 'error'
      });
      return {
        handled: 'fax'
      };
    }

    try {
      printBuffer(result);
      return {
        handled: 'print'
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Print failed. Invalid response from server.';
      setFaxAlertInfo({
        text: message,
        severity: 'error'
      });
      return {
        handled: 'print'
      };
    }
  }, [requestFax]);
  const handleCloseAlert = useCallback(() => setFaxAlertInfo(null), []);
  const value = {
    requestFax,
    submitPrintOrFax,
    isFaxEnabled
  };
  return /*#__PURE__*/React.createElement(FaxContext.Provider, {
    value: value
  }, children, /*#__PURE__*/React.createElement(FaxModal, {
    open: open,
    onClose: handleClose,
    siteFaxList: siteFaxList,
    faxOption: faxOption,
    setFaxOption: setFaxOption,
    onSubmit: handleSubmit
  }), faxAlertInfo && /*#__PURE__*/React.createElement(APIAlert, {
    errorInfo: faxAlertInfo,
    handleClose: handleCloseAlert,
    persistentError: !!faxAlertInfo.persistent
  }));
};